import { apiClient } from '@shared/api';
import type { ApiResponse } from '@shared/types';
import type { ItemStockDto, ItemStockSummaryDto, BatchStockRequestDto } from '../types';

class StockService {
  private readonly baseUrl = '/inventory';

  /**
   * Stock TOTALS per item, broken down per bin, with no lot/serial rows — the read behind every
   * availability display (badge, cart, max-quantity clamp).
   *
   * ⚠️ Prefer this over `getStockBatch`. The detail route returns one row per bin PER SERIAL, so a
   * serial-tracked item returns one row per unit on hand: the four-item picklist screen that
   * surfaced this read 1,286 rows to draw four badges. This answers the same screen in 6.
   */
  async getStockSummaryBatch(itemIds: number[]): Promise<ItemStockSummaryDto[]> {
    if (itemIds.length === 0) {
      return [];
    }

    const request: BatchStockRequestDto = { itemIds };

    const { data } = await apiClient.post<ApiResponse<ItemStockSummaryDto[]>>(
      `${this.baseUrl}/stock/summary`,
      request
    );

    if (data.success && data.data) {
      return data.data;
    }

    throw new Error(data.message || 'Failed to fetch stock data');
  }

  /**
   * Full SERIAL-LEVEL stock for specific items — one entry per bin per serial/lot.
   *
   * ⚠️ Only for callers that need the serial identity itself (the bin allocation dialog). Anything
   * that just shows availability wants `getStockSummaryBatch`. Fetch this lazily, per item, at the
   * moment the serials are needed — never for a whole screen of items up front.
   */
  async getStockBatch(itemIds: number[]): Promise<ItemStockDto[]> {
    if (itemIds.length === 0) {
      return [];
    }

    const request: BatchStockRequestDto = { itemIds };

    const { data } = await apiClient.post<ApiResponse<ItemStockDto[]>>(
      `${this.baseUrl}/stock`,
      request
    );

    if (data.success && data.data) {
      return data.data;
    }

    throw new Error(data.message || 'Failed to fetch stock data');
  }

  // PT-6 retired `getReplacementSerials` with its route: the bin card's Swap serial reads
  // `picklistService.getReplacementSerials` (PT-2's swap list, statuses labelled, never hidden).
}

export const stockService = new StockService();
